import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { FollowPropertyModalWithData } from '../../components/PropertyRelationship/subcomponents/FollowPropertyModal/FollowPropertyModalWithData';
import { UpdatePropertyRelationshipModalWithData } from '../../components/PropertyRelationship/subcomponents/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModalWithData';
import { UpdatePropertyDetailsModalWithData } from '../../components/UpdatePropertyDetailsModal/UpdatePropertyDetailsModalWithData';
import { BeforeYouBuyModalWithData } from '../../components/BeforeYouBuyModal/BeforeYouBuyModal';
import { SupplyDemandPerformanceDisclaimer } from '../../components/MarketInsightsBrick/subcomponents/SupplyDemandPerformanceDisclaimer/SupplyDemandPerformanceDisclaimer';
import { NearbyChildcareDisclaimer } from '../../../components/NearbyChildcareDisclaimer/NearbyChildcareDisclaimer';
import { NearbySchoolsDisclaimer } from '../../../components/NearbySchoolsDisclaimer/NearbySchoolsDisclaimer';
import { PropertyValuationDisclaimer } from '../../components/PropertyValuationBrick/subcomponents/PropertyValuationDisclaimer/PropertyValuationDisclaimer';
import { PropertyFeaturesDisclaimer } from '../../components/PropertyFeaturesBrick/subcomponents/PropertyFeaturesDisclaimer/PropertyFeaturesDisclaimer';
import { EnergyEfficiencyDisclaimer } from '../../components/PropertyFeaturesBrick/subcomponents/EnergyEfficiencyBrick/subcomponents/EnergyEfficiencyDisclaimer/EnergyEfficiencyDisclaimer';
import { NearbyPlanningPermitsDisclaimer } from '../../components/NearbyPlanningPermitsBrick/subcomponents/NearbyPlanningPermitsDisclaimer/NearbyPlanningPermitsDisclaimer';
import { InternetAvailabilityDisclaimer } from '../../components/InternetAvailabilityBrick/subcomponents/InternetAvailabilityDisclaimer/InternetAvailabilityDisclaimer';
import { GovernmentDataDisclaimer } from '../../components/GovernmentDataDisclaimer/GovermentDataDisclaimer';
import { PropertyPageDataDisclaimerModal } from '../../components/PropertyPageDataDisclaimerModal/PropertyPageDataDisclamierModal';
import { GovernmentOverlaysBrickDataDisclaimerModal } from '../../components/GovernmentOverlaysBrick/subcomponents/GovernmentOverlaysBrickDataDisclaimerModal/GovernmentOverlaysBrickDataDisclamierModal';
import { GovernmentOverlaysBrickInformationModal } from '../../components/GovernmentOverlaysBrick/subcomponents/GovernmentOverlaysBrickInformationModal/GovernmentOverlaysBrickInformationModal';

interface UpdatePropertyRelationshipModal {
  name: 'updatePropertyRelationshipModal';
}
interface FollowPropertyModal {
  name: 'followPropertyModal';
}
interface FollowPropertyModalWithMyHomeAsDefault {
  name: 'followPropertyModalWithMyHomeAsDefault';
}
interface UpdatePropertyDetailsModal {
  name: 'updatePropertyDetailsModal';
  entryPoint: string;
}
interface BeforeYouBuyModal {
  name: 'beforeYouBuyModal';
}
interface UpdatePropertyRelationshipModalMyHomeAsDefault {
  name: 'updatePropertyRelationshipModalMyHomeAsDefault';
}
interface SupplyDemandPerformanceDisclaimerModal {
  name: 'supplyDemandPerformanceDisclaimer';
}
interface NearbySchoolsDisclaimerModal {
  name: 'nearbySchoolsDisclaimer';
}
interface NearbyChildcareDisclaimerModal {
  name: 'nearbyChildcareDisclaimer';
}

interface AVMDisclaimerModal {
  name: 'avmDisclaimer';
}
interface EnergyEfficiencyDisclaimerModal {
  name: 'energyEfficiencyDisclaimer';
}
interface InternetAvailabilityDisclaimerModal {
  name: 'internetAvailabilityDisclaimer';
}
interface PropertyFeaturesDisclaimerModal {
  name: 'propertyFeaturesDisclaimer';
}
interface NearbyPlanningPermitsDisclaimerModal {
  name: 'nearbyPlanningPermitsDisclaimer';
}

interface GovernmentDataDisclaimerModal {
  name: 'governmentDataDisclaimer';
}

interface PropertyPageDataDisclaimerModal {
  name: 'propertyPageDataDisclaimer';
}

interface GvernmentOverlaysBrickDataDisclaimerModal {
  name: 'governmentOverlaysBrickDataDisclaimer';
}

interface GvernmentOverlaysBrickInformationModal {
  name: 'governmentOverlaysBrickInformation';
}

type PropertyPageModal =
  | FollowPropertyModal
  | FollowPropertyModalWithMyHomeAsDefault
  | UpdatePropertyRelationshipModal
  | UpdatePropertyDetailsModal
  | BeforeYouBuyModal
  | UpdatePropertyRelationshipModalMyHomeAsDefault
  | SupplyDemandPerformanceDisclaimerModal
  | NearbyChildcareDisclaimerModal
  | NearbySchoolsDisclaimerModal
  | AVMDisclaimerModal
  | EnergyEfficiencyDisclaimerModal
  | InternetAvailabilityDisclaimerModal
  | NearbyPlanningPermitsDisclaimerModal
  | PropertyFeaturesDisclaimerModal
  | GovernmentDataDisclaimerModal
  | PropertyPageDataDisclaimerModal
  | GvernmentOverlaysBrickDataDisclaimerModal
  | GvernmentOverlaysBrickInformationModal
  | null;

interface PropertyPageModalContext {
  displayModal: (x: PropertyPageModal) => void;
}

export const PropertyPageModalContext = createContext<PropertyPageModalContext | undefined>(
  undefined,
);

export const usePropertyPageModalContext = (): PropertyPageModalContext => {
  const context = useContext(PropertyPageModalContext);
  if (context === undefined) {
    throw new Error(
      'PropertyPageModalContext must be used within a PropertyPageModalContext.Provider',
    );
  }
  return context;
};

interface PropertyPageModalHandlerProps {
  modal: PropertyPageModal;
  onClose: () => void;
}

export const PropertyPageModalHandler: FunctionComponent<PropertyPageModalHandlerProps> = ({
  modal,
  onClose,
}) => {
  switch (modal?.name) {
    case 'followPropertyModal':
      return <FollowPropertyModalWithData opened onClose={onClose} />;
    case 'followPropertyModalWithMyHomeAsDefault':
      return <FollowPropertyModalWithData opened onClose={onClose} defaultRelationship />;
    case 'updatePropertyRelationshipModal':
      return <UpdatePropertyRelationshipModalWithData opened onClose={onClose} />;
    case 'updatePropertyDetailsModal':
      return (
        <UpdatePropertyDetailsModalWithData
          opened
          onClose={onClose}
          entryPoint={modal.entryPoint}
        />
      );
    case 'updatePropertyRelationshipModalMyHomeAsDefault':
      return (
        <UpdatePropertyRelationshipModalWithData opened onClose={onClose} defaultRelationship />
      );
    case 'beforeYouBuyModal':
      return <BeforeYouBuyModalWithData opened onClose={onClose} />;
    case 'supplyDemandPerformanceDisclaimer':
      return <SupplyDemandPerformanceDisclaimer opened onClose={onClose} />;
    case 'nearbyChildcareDisclaimer':
      return <NearbyChildcareDisclaimer opened onClose={onClose} />;
    case 'nearbySchoolsDisclaimer':
      return <NearbySchoolsDisclaimer opened onClose={onClose} />;
    case 'avmDisclaimer':
      return <PropertyValuationDisclaimer opened onClose={onClose} />;
    case 'energyEfficiencyDisclaimer':
      return <EnergyEfficiencyDisclaimer opened onClose={onClose} />;
    case 'internetAvailabilityDisclaimer':
      return <InternetAvailabilityDisclaimer opened onClose={onClose} />;
    case 'nearbyPlanningPermitsDisclaimer':
      return <NearbyPlanningPermitsDisclaimer opened onClose={onClose} />;
    case 'propertyFeaturesDisclaimer':
      return <PropertyFeaturesDisclaimer opened onClose={onClose} />;
    case 'governmentDataDisclaimer':
      return <GovernmentDataDisclaimer opened onClose={onClose} />;
    case 'propertyPageDataDisclaimer':
      return <PropertyPageDataDisclaimerModal opened onClose={onClose} />;
    case 'governmentOverlaysBrickDataDisclaimer':
      return <GovernmentOverlaysBrickDataDisclaimerModal opened onClose={onClose} />;
    case 'governmentOverlaysBrickInformation':
      return <GovernmentOverlaysBrickInformationModal opened onClose={onClose} />;
    default:
      return null;
  }
};

export const PropertyPageModalContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [modal, setModal] = useState<PropertyPageModal>(null);
  const contextValue = useMemo(
    () => ({
      displayModal: (a: PropertyPageModal) => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setModal(a);
          }, 0);
        });
      },
    }),
    [setModal],
  );

  return (
    <PropertyPageModalContext.Provider value={contextValue}>
      {modal && <PropertyPageModalHandler modal={modal} onClose={() => setModal(null)} />}
      {children}
    </PropertyPageModalContext.Provider>
  );
};

export const modalZIndex = 500;
